import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  find, first, values, each,
  concat, delay, last, map, reduce,
  reduceRight, omit,
} from 'lodash';
import moment from 'moment';
import { AgGridReact } from 'ag-grid-react';
import { Dropdown, MenuItem } from 'react-bootstrap';
import ProgressBar from '../../../../components/progress-bar';
import styles from './styles.css';
import OwnerSelector from '../../../../components/owner-selector';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import { getViewableDimensions } from '../../../App/AppReducer';
import SalesGoalSelector from './sales-goal-selector';
import LabelRendered from './LabelCellRenderer';
import StageCellRenderer from './StageCellRenderer';
import InsightCellRendered from './InsightCellRenderer';
import {
  getConversionGraphLoading,
  getConversionGraphLoaded,
  getVelocityGraphOwners,
  getStages,
  getProjectedGraphGlobalStats,
  getProjectedGraphOwnerStats,
  getConversionGraphAverage,
  getConversionGraphOwners,
  getConversionGraphHistoricalSales,
} from '../../PipelineReducer';

const periodOptions = [
  { label: '60 Days', value: '60-days', months: 2 },
  { label: '90 Days', value: '90-days', months: 3 },
  { label: '4 Months', value: '4-months', months: 4 },
  { label: '5 Months', value: '5-months', months: 5 },
  { label: '6 Months', value: '6-months', months: 6 },
];

class ProjectedGraph extends Component {
  constructor(props) {
    super(props);
    this.state = {
      reportPeriod: '90-days', // default
      salesGoal: 0,
      ownerId: null,
      months: [],
      totalStats: {},
      insights: {},
    };
    this.setReportPeriod = this.setReportPeriod.bind(this);
    this.handleSelectOwner = this.handleSelectOwner.bind(this);
    this.handleSalesGoalChange = this.handleSalesGoalChange.bind(this);
    this.onGridReady = this.onGridReady.bind(this);
    this.getStageCellRenderParams = this.getStageCellRenderParams.bind(this);
    this.gatherData = this.gatherData.bind(this);
    this.getTotalCellRenderParams = this.getTotalCellRenderParams.bind(this);
    this.showOpportunities = this.showOpportunities.bind(this);
    this.getInsightCellRenderParams = this.getInsightCellRenderParams.bind(this);
    this.rowTypeValueGetter = this.rowTypeValueGetter.bind(this);
  }

  componentDidMount() {
    if (!this.props.loading && this.props.loaded) {
      this.calculateDefaultSalesGoal(this.gatherData);
    }
  }

  componentWillReceiveProps(nextProps) {
    if ((this.props.loading !== nextProps.loading || this.props.loaded !== nextProps.loaded) && nextProps.loaded) {
      delay(() => {
        this.calculateDefaultSalesGoal(this.gatherData);
      }, 500);
    }
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  setReportPeriod(reportPeriod) {
    this.setState({ reportPeriod }, () => {
      this.calculateDefaultSalesGoal(this.gatherData);
    });
  }

  getMonths() {
    const reportPeriod = find(periodOptions, { value: this.state.reportPeriod }) || first(periodOptions);
    const totalMonths = reportPeriod.months || 1;
    const start = moment();
    const end = start.clone().add(totalMonths - 1, 'months');
    const months = [start];
    const i = start.clone();
    while (i.isBefore(end, 'month')) {
      i.add(1, 'months');
      months.push(i.clone());
    }
    return months;
  }
  getStageCellRenderParams(params) {
    if (!params.colDef) {
      return null;
    }
    const { data, colDef: { cellRendererParams: { dateKey } } } = params;
    const { ownerId } = this.state;
    const { projectedGraphOwnerStats, projectedGraphGlobalStats } = this.props;
    let stats = null;
    if (ownerId && projectedGraphOwnerStats[ownerId] && projectedGraphOwnerStats[ownerId][dateKey]) {
      stats = projectedGraphOwnerStats[ownerId][dateKey];
    } else if (!ownerId && projectedGraphGlobalStats[dateKey]) {
      stats = projectedGraphGlobalStats[dateKey];
    }
    return {
      component: 'stageCellRenderer',
      params: {
        isProjectedSales: data.isProjectedSales,
        rowType: data.rowType,
        stats,
      },
    };
  }

  getTotalCellRenderParams(params) {
    if (!params.colDef) {
      return null;
    }
    const { data } = params;
    return {
      component: 'stageCellRenderer',
      params: {
        isProjectedSales: data.isProjectedSales,
        rowType: data.rowType,
        stats: this.state.totalStats,
      },
    };
  }

  getInsightCellRenderParams(params) {
    if (!params.colDef) {
      return null;
    }
    const { data } = params;
    return {
      component: 'insightCellRendered',
      params: {
        isProjectedSales: data.isProjectedSales,
        stats: this.state.insights,
        rowType: data.rowType,
      },
    };
  }
  showOpportunities(opportunities) {
    this.props.onSelect(map(opportunities, 'opportunity_id'));
  }

  gatherData() {
    const months = this.getMonths();
    // total columns
    const {
      projectedGraphOwnerStats,
      projectedGraphGlobalStats,
      conversionGraphOwners,
      conversionGraphAverage,
      stages,
    } = this.props;
    const { salesGoal, ownerId } = this.state;
    let stats = null;
    let stageRates = {}; // indexed by stage value
    let lastStageWinProbabilityRate = null;

    if (ownerId && projectedGraphOwnerStats[ownerId]) {
      stats = projectedGraphOwnerStats[ownerId];
    } else if (!ownerId && projectedGraphGlobalStats) {
      stats = projectedGraphGlobalStats;
    }
    if (ownerId && conversionGraphOwners[ownerId]) {
      stageRates = reduceRight(conversionGraphOwners[ownerId], (items, stage) => {
        if (stage) {
          items[stage.value] = stage.amountProbabilityRate;
          if (lastStageWinProbabilityRate === null) {
            lastStageWinProbabilityRate = stage.amountProbabilityRate;
          }
        }
        return items;
      }, {});
    } else if (!ownerId && projectedGraphGlobalStats) {
      stageRates = reduce(conversionGraphAverage, (items, stage) => {
        items[stage.value] = stage.averageAmountProbabilityRate;
        if (lastStageWinProbabilityRate === null) {
          lastStageWinProbabilityRate = stage.averageAmountProbabilityRate;
        }
        return items;
      }, {});
    }

    // Days Left in Period = (Count of days left in current month) + (each additional month's days)
    let remainingDays = 0;
    const lastMonth = last(months);
    if (lastMonth) {
      remainingDays = lastMonth.clone().endOf('month').diff(moment(), 'days');
    }

    const insights = {
      salesGoalAttainment: 0,
      pipelineNeeded: 0,
      winRateNeeded: 0,
      winRateGap: 0,
      stages: {},
    };
    const total = {
      totalOpportunities: [],
      totalProjectedSales: 0,
      totalAmount: 0,

      oldOpportunities: [],
      oldProjectedSales: 0,
      oldProjectedSalesPercentage: 0,

      bigOpportunities: [],
      bigProjectedSales: 0,
      bigProjectedSalesPercentage: 0,
      stages: {

      }, // indexed by stage
    };
    if (stats) {
      each(months, (month) => {
        const dateKey = month.format('MM-YYYY');
        if (stats[dateKey]) {
          const monthStats = stats[dateKey];
          total.totalOpportunities = concat(total.totalOpportunities, monthStats.totalOpportunities);
          total.oldOpportunities = concat(total.oldOpportunities, monthStats.oldOpportunities);
          total.bigOpportunities = concat(total.bigOpportunities, monthStats.bigOpportunities);
          total.totalAmount += monthStats.totalAmount;
          total.totalProjectedSales += monthStats.totalProjectedSales || 0;
          total.bigProjectedSales += monthStats.bigProjectedSales || 0;
          total.oldProjectedSales += monthStats.oldProjectedSales || 0;
          each(monthStats.stages, (monthStage, stageValue) => {
            const stage = find(stages, { value: stageValue });
            if (stage && !stage.skip_conversion) {
              if (!total.stages[stageValue]) {
                total.stages[stageValue] = {
                  oldOpportunities: [],
                  bigOpportunities: [],
                  totalOpportunities: [],
                  totalAmount: 0,
                  bigAmount: 0,
                  bigAmountPercentage: 0,
                  oldAmount: 0,
                  oldAmountPercentage: 0,
                };
              }
              const totalStageStats = total.stages[stageValue];
              totalStageStats.totalOpportunities = concat(totalStageStats.totalOpportunities, monthStage.totalOpportunities);
              totalStageStats.bigOpportunities = concat(totalStageStats.bigOpportunities, monthStage.bigOpportunities);
              totalStageStats.oldOpportunities = concat(totalStageStats.oldOpportunities, monthStage.oldOpportunities);
              totalStageStats.totalAmount += monthStage.totalAmount;
              totalStageStats.bigAmount += monthStage.bigAmount;
              totalStageStats.oldAmount += monthStage.oldAmount;

              if (!insights.stages[stageValue]) {
                insights.stages[stageValue] = {
                  amount: 0,
                  projectedAmount: 0,
                  opportunities: [],

                  thisAmount: 0,
                  thisProjectedAmount: 0,
                  thisProjectedPercentage: 0,
                  thisOpportunities: [],

                  nextAmount: 0,
                  nextProjectedAmount: 0,
                  nextProjectedPercentage: 0,
                  nextOpportunities: [],
                };
              }
              each(monthStats.totalOpportunities, (opportunity) => {
                const withinPeriod = opportunity.days_to_close < remainingDays;
                if (withinPeriod) {
                  insights.stages[stageValue].thisAmount += opportunity.amount;
                  insights.stages[stageValue].thisOpportunities.push(opportunity);
                } else {
                  insights.stages[stageValue].nextAmount += opportunity.amount;
                  insights.stages[stageValue].nextOpportunities.push(opportunity);
                }
                insights.stages[stageValue].amount += opportunity.amount;
                insights.stages[stageValue].opportunities.push(opportunity);
              });
            }
          });
        }
      });

      if (total.totalProjectedSales) {
        total.oldProjectedSalesPercentage = total.oldProjectedSales / total.totalProjectedSales;
        total.bigProjectedSalesPercentage = total.bigProjectedSales / total.totalProjectedSales;
      }
      each(total.stages, (totalStage) => {
        if (totalStage.totalAmount) {
          totalStage.bigAmountPercentage = totalStage.bigAmount / totalStage.totalAmount;
          totalStage.oldAmountPercentage = totalStage.oldAmount / totalStage.totalAmount;
        }
      });

      each(insights.stages, (insightsStage, stageName) => {
        const wpr = stageRates[stageName] || 1; // get stage win probability rate
        insightsStage.projectedAmount = insightsStage.amount * wpr;
        insightsStage.thisProjectedAmount = insightsStage.thisAmount * wpr;
        insightsStage.nextProjectedAmount = insightsStage.nextAmount * wpr;
        if (insightsStage.projectedAmount) {
          insightsStage.thisProjectedPercentage = insightsStage.thisProjectedAmount / insightsStage.projectedAmount;
          insightsStage.nextProjectedPercentage = insightsStage.nextProjectedAmount / insightsStage.projectedAmount;
        }
      });
      insights.salesGoalAttainment = total.totalProjectedSales - salesGoal;
      if (lastStageWinProbabilityRate !== null && lastStageWinProbabilityRate !== 0) {
        insights.pipelineNeeded = Math.abs(insights.salesGoalAttainment) / lastStageWinProbabilityRate;
        if (insights.salesGoalAttainment >= 0) {
          insights.winRateNeeded = total.totalAmount !== 0 ? salesGoal / total.totalAmount : 0;
          insights.winRateGap = lastStageWinProbabilityRate - insights.winRateNeeded;
        } else {
          insights.winRateNeeded = total.totalAmount !== 0 ? Math.abs(insights.salesGoalAttainment) / total.totalAmount : 0;
          insights.winRateGap = insights.winRateNeeded - lastStageWinProbabilityRate;
        }
      }
    }
    this.setState({
      months,
      insights,
      totalStats: total,
    }, () => {
      delay(() => {
        // let the grid initialize
        if (this.gridApi) {
          this.gridApi.refreshCells({ force: true });
          this.gridColumnApi.autoSizeColumns();
        }
      }, 1000);
    });
  }

  handleSelectOwner(owner) {
    this.setState({ ownerId: owner ? owner.user_id : null }, () => {
      this.calculateDefaultSalesGoal(this.gatherData);
    });
  }

  calculateDefaultSalesGoal(cb = () => {}) {
    const { historicalSales } = this.props;
    const reportPeriod = find(periodOptions, { value: this.state.reportPeriod }) || first(periodOptions);
    const totalMonths = reportPeriod.months || 1;
    const start = moment();
    const end = start.clone().subtract(totalMonths - 1, 'months');
    const months = [start.format('MM-YYYY')];
    const i = start.clone();
    while (i.isAfter(end, 'month')) {
      i.subtract(1, 'months');
      months.push(i.clone().format('MM-YYYY'));
    }
    const { ownerId } = this.state;
    const historicalAmounts = (ownerId && historicalSales.owners && historicalSales.owners[ownerId]) ? historicalSales.owners[ownerId] : historicalSales.global;
    let salesGoal = 0;
    each(months, (dateKey) => {
      if (historicalAmounts[dateKey]) {
        salesGoal += historicalAmounts[dateKey];
      }
    });
    return this.setState({ salesGoal }, cb);
  }

  handleSalesGoalChange(salesGoal) {
    this.setState({ salesGoal }, this.gatherData);
  }

  rowTypeValueGetter(params) {
    if (params.data) {
      let label = null;
      const { rowType } = params.data;
      if (params.data.skip_conversion) {
        label = '';
      } else if (params.data.isProjectedSales) {
        if (rowType === 'total') {
          label = 'Total';
        } else if (rowType === 'big') {
          label = 'From big deals';
        } else if (rowType === 'old') {
          label = 'From old deals';
        }
      } else if (rowType === 'total') {
        label = 'Pipeline';
      } else if (rowType === 'big') {
        label = 'Big deals';
      } else if (rowType === 'old') {
        label = 'Old deals';
      }
      return label;
    }
    return null;
  }

  renderControls() {
    const { organization } = this.props;
    const { reportPeriod } = this.state;
    const activePeriod = find(periodOptions, { value: reportPeriod }) || first(periodOptions);

    return (
      <h5 className="sub-header-with-controls">
        <OwnerSelector
          owners={this.props.owners}
          ownerId={this.state.ownerId}
          onSelect={this.handleSelectOwner}
          emptyText="Total"
        />
        &nbsp;pipeline risk based on&nbsp;
        <Dropdown
          id="period-dropdown"
        >
          <Dropdown.Toggle bsSize="small">
            {activePeriod.label}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {periodOptions.map(s => (
              <MenuItem
                key={s.value}
                active={s.value === activePeriod.value}
                onSelect={() => { this.setReportPeriod(s.value); }}
              >
                {s.label}
              </MenuItem>
              ))}
          </Dropdown.Menu>
        </Dropdown>
        &nbsp;sales goal of&nbsp;
        <SalesGoalSelector
          salesGoal={this.state.salesGoal}
          organization={organization}
          onSalesGoalChange={this.handleSalesGoalChange}
        />
      </h5>
    );
  }

  renderTable() {
    const { stages, viewableDimensions: { width } } = this.props;
    const { months } = this.state;
    const rowTypeWidth = 120;
    const insightWidth = 250;
    const totalColumns = 1 + months.length + 1;
    const columnWidth = Math.max(120, Math.floor((width - rowTypeWidth - insightWidth - 1) / totalColumns));

    const colDefs = [
      {
        headerName: '',
        field: 'label',
        cellRenderer: 'labelRenderer',
        pinned: 'left',
        width: columnWidth,
        cellStyle: {
          'white-space': 'normal',
        },
        rowSpan: (params) => {
          if (params.data && params.data.rowType === 'total' && !params.data.skip_conversion) {
            return 3;
          }
          return 1;
        },
        cellClass: (params) => {
          if (params.data && params.data.rowType === 'total') {
            return styles.rowSpan;
          }
        },
      },
      {
        headerName: '',
        field: 'rowType',
        width: rowTypeWidth,
        pinned: 'left',
        resizable: false,
        valueGetter: this.rowTypeValueGetter,
      },
      ...(months.map(m => ({
        headerName: m.format('MMM Y'),
        width: columnWidth,
        cellRendererParams: {
          dateKey: m.format('MM-YYYY'),
          onSelect: this.showOpportunities,
        },
        cellRendererSelector: this.getStageCellRenderParams,
      }))),
      {
        headerName: 'Total',
        width: columnWidth,
        pinned: 'right',
        cellRendererParams: {
          onSelect: this.showOpportunities,
        },
        cellRendererSelector: this.getTotalCellRenderParams,
        cellClass: () => styles.totalColumn,
      },
      {
        headerName: 'Insights',
        width: insightWidth,
        cellRendererParams: {
          onSelect: this.showOpportunities,
        },
        pinned: 'right',
        cellStyle: {
          'white-space': 'normal',
        },
        cellRendererSelector: this.getInsightCellRenderParams,
        rowSpan: (params) => {
          if (params.data && params.data.rowType === 'total' && !params.data.skip_conversion) {
            return 3;
          }
          return 1;
        },
        cellClass: (params) => {
          if (params.data && params.data.rowType === 'total') {
            if (params.data.isProjectedSales) {
              return styles.rowSpan;
            }
            return `${styles.rowSpan} ${styles.insightCell}`;
          }
          return styles.insightCell;
        },
      },
      {
        headerName: 'Conversion',
        field: 'group',
        hide: true,
        rowGroup: true,
        rowGroupIndex: 0,
      },
    ];
    const rowTypes = ['total', 'big', 'old'];
    
    const allRows = [
      ...(rowTypes.map(rowType => ({
        rowId: rowType,
        isProjectedSales: true,
        rowType,
        ...(rowType === 'total' ? { label: 'Projected Sales' } : {}),
      }))),
      ...(reduce(stages, (items, stage) => {
        const rows = stage.skip_conversion ? ['total'] : rowTypes;
        each(rows, (rowType) => {
          items.push({
            ...omit(stage, ['label']),
            rowId: `${stage._id}-${rowType}`,
            group: stage.conversion_stage === 'early' ? 'Early Stages' : 'Late Stages',
            rowType,
            ...(rowType === 'total' ? { label: stage.label } : {}),
          });
        });
        return items;
      }, [])),
    ];
    return (
      <div className={`ag-theme-balham ${styles.table}`} style={{ width: '100%' }}>
        <AgGridReact
          reactNext
          columnDefs={colDefs}
          enableColResize
          getRowNodeId={data => data.rowId}
          onGridReady={this.onGridReady}
          rowData={allRows}
          domLayout="autoHeight"
          groupUseEntireRow
          groupRowRendererParams={{
            suppressCount: true,
          }}
          suppressRowTransform
          groupDefaultExpanded={-1}
          getRowClass={(params) => {
            if (params.node.group) {
              return styles.groupRow;
            }
            if (params.data && params.data.rowType === 'total') {
              return styles.lastRow;
            }
          }}
          defaultColDef={{
            suppressMenu: true,
            suppressMovable: true,
            suppressColumnsToolPanel: true,
            suppressFiltersToolPanel: true,
            resizable: true,
          }}
          frameworkComponents={{
            labelRenderer: LabelRendered,
            stageCellRenderer: StageCellRenderer,
            insightCellRendered: InsightCellRendered,
          }}
        />
      </div>
    );
  }

  render() {
    const { loading, loaded } = this.props;
    return (
      <div className={styles.chart}>
        {(loading || !loaded) && <ProgressBar />}
        {!loading && loaded &&
          <div>
            {this.renderControls()}
            {this.renderTable()}
          </div>
        }
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    stages: getStages(state),
    organization: getUserCurrentOrganization(state),
    loaded: getConversionGraphLoaded(state),
    loading: getConversionGraphLoading(state),
    owners: values(getVelocityGraphOwners(state)),
    viewableDimensions: getViewableDimensions(state),
    projectedGraphGlobalStats: getProjectedGraphGlobalStats(state),
    projectedGraphOwnerStats: getProjectedGraphOwnerStats(state),
    conversionGraphAverage: getConversionGraphAverage(state),
    conversionGraphOwners: getConversionGraphOwners(state),
    historicalSales: getConversionGraphHistoricalSales(state),
  };
}

ProjectedGraph.propTypes = {
  loaded: PropTypes.bool,
  loading: PropTypes.bool,
  onSelect: PropTypes.func,
  owners: PropTypes.arrayOf(PropTypes.shape({})),
  organization: PropTypes.shape({
    organization_id: PropTypes.string.isRequired,
  }),
  stages: PropTypes.arrayOf(PropTypes.shape({})),
  viewableDimensions: PropTypes.shape({}).isRequired,
  projectedGraphGlobalStats: PropTypes.shape({}).isRequired,
  projectedGraphOwnerStats: PropTypes.shape({}).isRequired,
  conversionGraphAverage: PropTypes.arrayOf(PropTypes.shape({})),
  conversionGraphOwners: PropTypes.shape({}), // indexed by owner id
  historicalSales: PropTypes.shape({
    global: PropTypes.shape({}),
    owners: PropTypes.shape({}),
  }).isRequired,
};

ProjectedGraph.defaultProps = {
  organization: null,
  loaded: false,
  loading: false,
  onSelect: () => {},
  owners: [],
  stages: [],
  conversionGraphAverage: [],
  conversionGraphOwners: {},
};
ProjectedGraph.contextTypes = {
  updateUserPreference: PropTypes.func.isRequired,
  userPreferences: PropTypes.shape({}).isRequired,
  addNotification: PropTypes.func.isRequired,
};
export default connect(mapStateToProps)(ProjectedGraph);
